import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, ArrowUpRight, Check, ChevronDown, Sparkles, Layers, Type, Palette,
  ShieldCheck, Accessibility, Download, Copy, FileText, Lock, MessageCircle,
  Crown, Building2, Users, Github, Linkedin, ExternalLink, Sun, Moon,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import BrandMonogram from "@/components/BrandMonogram";
import { FAQ_GROUPS, buildFaqJsonLd, buildBreadcrumbJsonLd } from "@/data/faqData";

const DOMAIN = process.env.REACT_APP_DOMAIN_NAME || "brandfinish.com";

/* =========================================================================
   BrandFinish – Public Home / Marketing Site
   Long-form, indexable, self-serve pre-sales. Uses existing design tokens.
   ========================================================================= */

const PLAN_INTERNAL_TO_DISPLAY = {
  launch: "Freelancer",
  creator: "Boutique",
  studio: "Agency",
  pro: "Enterprise",
};

function SectionLabel({ index, children, testId }) {
  return (
    <div
      data-testid={testId}
      className="text-[10px] font-mono uppercase tracking-[0.28em]"
      style={{ color: "var(--bf-fg-subtle)" }}
    >
      {index} / {children}
    </div>
  );
}

function PrimaryCTA({ to, children, testId, onClick }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      onClick={onClick}
      className="bf-btn-primary px-6 py-3.5 text-sm font-semibold rounded-md inline-flex items-center gap-2 transition-all"
    >
      {children}
    </Link>
  );
}

function GhostCTA({ to, href, children, testId, onClick }) {
  const cls =
    "px-6 py-3.5 text-sm font-semibold rounded-md inline-flex items-center gap-2 border transition-colors";
  const style = { borderColor: "var(--bf-line-medium)", color: "var(--bf-fg)" };
  if (href) {
    return (
      <a href={href} data-testid={testId} onClick={onClick} className={cls} style={style}>
        {children}
      </a>
    );
  }
  return (
    <Link to={to} data-testid={testId} onClick={onClick} className={cls} style={style}>
      {children}
    </Link>
  );
}

/* ----------------------------------------------------------------------- */
/* Top Nav                                                                  */
/* ----------------------------------------------------------------------- */
function HomeNav({ user }) {
  const [scrolled, setScrolled] = useState(false);
  const { theme, toggle } = useTheme();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <nav
      data-testid="home-nav"
      className={`fixed top-0 left-0 right-0 z-40 transition-all ${
        scrolled ? "backdrop-blur-xl border-b" : "border-b border-transparent"
      }`}
      style={{
        background: scrolled ? "color-mix(in oklab, var(--bf-bg) 82%, transparent)" : "transparent",
        borderColor: scrolled ? "var(--bf-line-medium)" : "transparent",
      }}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-[64px] flex items-center justify-between">
        <a href="#top" className="flex items-center gap-2.5" data-testid="home-nav-brand">
          <BrandMonogram size={28} />
          <div className="leading-tight">
            <div className="font-editorial text-[18px] tracking-[-0.01em]">BrandFinish</div>
            <div className="text-[9px] font-mono uppercase tracking-[0.28em]" style={{ color: "var(--bf-fg-subtle)" }}>
              Studio
            </div>
          </div>
        </a>
        <div className="hidden md:flex items-center gap-7 text-[12px] font-mono uppercase tracking-[0.18em]" style={{ color: "var(--bf-fg-muted)" }}>
          <a href="#features" data-testid="nav-features" className="hover:text-[var(--bf-fg)] transition-colors">Features</a>
          <a href="#how-it-works" data-testid="nav-how" className="hover:text-[var(--bf-fg)] transition-colors">How it works</a>
          <a href="#pricing" data-testid="nav-pricing" className="hover:text-[var(--bf-fg)] transition-colors">Pricing</a>
          <a href="#about" data-testid="nav-about" className="hover:text-[var(--bf-fg)] transition-colors">About</a>
          <a href="#faq" data-testid="nav-faq" className="hover:text-[var(--bf-fg)] transition-colors">FAQ</a>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={toggle}
            data-testid="home-theme-toggle"
            aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            className="inline-flex items-center justify-center w-9 h-9 rounded-md border transition-colors"
            style={{ borderColor: "var(--bf-line-medium)", color: "var(--bf-fg-muted)" }}
          >
            {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
          </button>
          {user ? (
            <Link
              to="/dashboard"
              data-testid="nav-dashboard-cta"
              className="bf-btn-primary px-4 py-2 text-[12px] font-semibold rounded-md inline-flex items-center gap-1.5"
            >
              Open dashboard <ArrowRight size={13} />
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="nav-signin"
                className="text-[12px] font-mono uppercase tracking-[0.18em] hover:text-[var(--bf-fg)] transition-colors px-2"
                style={{ color: "var(--bf-fg-muted)" }}
              >
                Sign in
              </Link>
              <Link
                to="/register"
                data-testid="nav-get-started"
                className="bf-btn-primary px-4 py-2 text-[12px] font-semibold rounded-md inline-flex items-center gap-1.5"
              >
                Get started <ArrowRight size={13} />
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}

/* ----------------------------------------------------------------------- */
/* Product UI Mock – small SVG/HTML renderings of the actual Workspace      */
/* (Not stock photos. Reflects what users actually see.)                    */
/* ----------------------------------------------------------------------- */
function ConceptBoardMock() {
  const cells = Array.from({ length: 12 });
  return (
    <div className="rounded-lg border bf-shadow-card overflow-hidden" style={{ borderColor: "var(--bf-line-medium)", background: "var(--bf-surface)" }}>
      <div className="px-4 py-2.5 flex items-center justify-between border-b" style={{ borderColor: "var(--bf-line-medium)" }}>
        <div className="flex items-center gap-2">
          <div className="flex gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: "var(--bf-line-medium)" }} />
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: "var(--bf-line-medium)" }} />
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: "var(--bf-violet)" }} />
          </div>
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] ml-3" style={{ color: "var(--bf-fg-subtle)" }}>
            Identity Concept Engine · 12 concepts
          </div>
        </div>
        <div className="text-[10px] font-mono uppercase tracking-[0.16em]" style={{ color: "var(--bf-violet)" }}>4 wordmarks · 4 symbols · 4 hybrid marks</div>
      </div>
      <div className="p-3 grid grid-cols-4 gap-2 bf-canvas">
        {cells.map((_, i) => {
          const fam = i < 4 ? "wm" : i < 8 ? "sym" : "hyb";
          return (
            <div
              key={i}
              className="aspect-square rounded-md border flex items-center justify-center text-[20px] font-editorial relative overflow-hidden"
              style={{
                borderColor: "var(--bf-line-medium)",
                background: i === 5 ? "linear-gradient(135deg, rgba(109,93,246,0.20), transparent 60%)" : "var(--bf-bg)",
              }}
            >
              {fam === "wm" && <span style={{ color: "var(--bf-fg)" }}>Bf</span>}
              {fam === "sym" && (
                <svg width="28" height="28" viewBox="0 0 28 28">
                  {i === 4 && <circle cx="14" cy="14" r="9" stroke="var(--bf-violet)" strokeWidth="2" fill="none" />}
                  {i === 5 && <rect x="6" y="6" width="16" height="16" stroke="var(--bf-champagne)" strokeWidth="2" fill="none" />}
                  {i === 6 && <polygon points="14,4 24,22 4,22" stroke="var(--bf-fg)" strokeWidth="2" fill="none" />}
                  {i === 7 && (
                    <>
                      <circle cx="10" cy="14" r="6" stroke="var(--bf-violet)" strokeWidth="2" fill="none" />
                      <circle cx="18" cy="14" r="6" stroke="var(--bf-fg)" strokeWidth="2" fill="none" />
                    </>
                  )}
                </svg>
              )}
              {fam === "hyb" && (
                <div className="flex items-center gap-1.5">
                  <span
                    className="w-3 h-3 rounded-sm"
                    style={{ background: i % 2 ? "var(--bf-violet)" : "var(--bf-champagne)" }}
                  />
                  <span style={{ color: "var(--bf-fg)" }} className="text-[14px]">Bf</span>
                </div>
              )}
              <div
                className="absolute bottom-1 left-1 text-[7px] font-mono uppercase tracking-wider px-1 rounded-sm"
                style={{ color: "var(--bf-fg-subtle)", background: "color-mix(in oklab, var(--bf-bg) 70%, transparent)" }}
              >
                {fam === "wm" ? "WORDMARK" : fam === "sym" ? "SYMBOL" : "HYBRID"}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PaletteMock() {
  const ramp = ["#F4F1FE", "#E5DFFC", "#C7BCF8", "#A48EF1", "#8470EC", "#6D5DF6", "#594BD8", "#4639B4", "#332888", "#251D5C", "#170F36"];
  return (
    <div className="rounded-lg border bf-shadow-card p-4" style={{ borderColor: "var(--bf-line-medium)", background: "var(--bf-surface)" }}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] font-mono uppercase tracking-[0.18em]" style={{ color: "var(--bf-fg-subtle)" }}>OKLCH ramp · primary</div>
        <div className="text-[10px] font-mono uppercase tracking-[0.16em]" style={{ color: "var(--bf-violet)" }}>WCAG AA · 4.81:1</div>
      </div>
      <div className="grid grid-cols-11 gap-1.5">
        {ramp.map((c, i) => (
          <div key={c} className="flex flex-col items-center">
            <div className="w-full aspect-square rounded-sm" style={{ background: c, border: "1px solid var(--bf-line-medium)" }} />
            <div className="text-[9px] font-mono mt-1" style={{ color: "var(--bf-fg-subtle)" }}>{i === 0 ? 50 : i * 100}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/* SECTION 1 – HERO                                                         */
/* ----------------------------------------------------------------------- */
function Hero({ user }) {
  return (
    <section id="top" className="relative overflow-hidden pt-[112px] pb-20" data-testid="home-hero">
      <div className="absolute inset-0 pointer-events-none bf-canvas opacity-50" />
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 70% 0%, rgba(109,93,246,0.18), transparent 55%)" }} />
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <SectionLabel index="00" testId="hero-label">Brand operating system · GBP · Stripe-billed</SectionLabel>
          <h1 className="font-editorial text-[56px] sm:text-[72px] lg:text-[88px] leading-[0.95] tracking-[-0.025em] mt-6">
            Strategy-led <em className="not-italic" style={{ color: "var(--bf-violet)" }}>brand identity</em>
            <br />software, finished.
          </h1>
          <p className="mt-7 text-[17px] leading-[1.65] max-w-[560px]" style={{ color: "var(--bf-fg-muted)" }}>
            BrandFinish takes a real client brief and outputs a complete identity system –
            twelve scored logo concepts, an OKLCH-correct colour ramp, typography routes,
            tokens, exports, and a markdown brand guide. Not a logo generator.
            A system designers actually ship.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-3">
            <PrimaryCTA to={user ? "/dashboard" : "/register"} testId="hero-primary-cta">
              Start designing <ArrowRight size={16} />
            </PrimaryCTA>
            <GhostCTA href="#how-it-works" testId="hero-secondary-cta">
              See how it works <ArrowUpRight size={15} />
            </GhostCTA>
          </div>
          <div className="mt-10 flex items-center gap-6 text-[11px] font-mono uppercase tracking-[0.18em]" style={{ color: "var(--bf-fg-subtle)" }}>
            <span className="hidden sm:flex items-center gap-1.5"><Check size={12} style={{ color: "var(--bf-violet)" }} /> Cancel anytime</span>
            <span className="hidden md:flex items-center gap-1.5"><Check size={12} style={{ color: "var(--bf-violet)" }} /> No card on Freelancer</span>
          </div>
        </div>
        <div className="lg:col-span-5 relative" data-testid="hero-product-preview">
          <ConceptBoardMock />
          <div className="mt-4">
            <PaletteMock />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ----------------------------------------------------------------------- */
/* SECTION 2 – WHAT IS BRANDFINISH                                          */
/* ----------------------------------------------------------------------- */
function WhatIs() {
  const pillars = [
    {
      title: "Brand systems, not isolated logos",
      body:
        "Most tools hand you a single mark and walk away. BrandFinish builds the whole system – wordmark, symbol, hybrid, monochrome, favicon, colour ramps, type routes, tokens, exports – in one coherent pass.",
    },
    {
      title: "Structured design intelligence",
      body:
        "Every output is grounded in a structured discovery – archetype, audience, style scales, accessibility requirements. Results are scored on originality, scalability, brand fit and small-size legibility, so you can argue the work in front of a client.",
    },
    {
      title: "Accessible by construction",
      body:
        "Palettes are generated in OKLCH so perceptual lightness is honest. Every ramp is checked against WCAG AA on white and the brand surface; the Accessibility Stress Test flags pairings that fail before they ship.",
    },
    {
      title: "Export-ready, real files",
      body:
        "SVG marks, CSS / SCSS / Tailwind starters, design tokens, a markdown brand guide and a ZIP asset pack – all real files, generated from your actual project. Free-tier exports are watermarked so client work stays paid work.",
    },
  ];
  return (
    <section id="what-is" className="relative py-24 border-t" style={{ borderColor: "var(--bf-line-medium)" }} data-testid="home-what-is">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <SectionLabel index="01">What is BrandFinish</SectionLabel>
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <h2 className="font-editorial text-[44px] lg:text-[56px] leading-[1.02] tracking-[-0.02em]">
              A platform for finishing brands,<br /><em className="not-italic" style={{ color: "var(--bf-violet)" }}>not just starting them</em>.
            </h2>
            <p className="mt-6 text-[15px] leading-[1.7] max-w-[480px]" style={{ color: "var(--bf-fg-muted)" }}>
              BrandFinish is brand identity software for independent creatives, design studios,
              creative agencies and in-house creative teams. It replaces the messy stack of
              moodboard tools, generic logo makers, ad-hoc colour pickers and copy-paste handover
              docs with one structured workflow.
            </p>
            <p className="mt-4 text-[15px] leading-[1.7] max-w-[480px]" style={{ color: "var(--bf-fg-muted)" }}>
              You bring the brief and the strategy. BrandFinish brings the system, the
              accessibility maths, the export pipeline and the version history – so the work
              you ship is the work you intended.
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-4">
            {pillars.map((p) => (
              <div
                key={p.title}
                className="rounded-lg border bf-shadow-card p-6"
                style={{ borderColor: "var(--bf-line-medium)", background: "var(--bf-surface)" }}
                data-testid={`what-is-pillar-${p.title.split(" ")[0].toLowerCase()}`}
              >
                <div className="text-[15px] font-medium mb-2" style={{ color: "var(--bf-fg)" }}>{p.title}</div>
                <div className="text-[13.5px] leading-[1.65]" style={{ color: "var(--bf-fg-muted)" }}>{p.body}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ----------------------------------------------------------------------- */
/* SECTION 3 – ORIGINS                                                      */
/* ----------------------------------------------------------------------- */
function Origins() {
  return (
    <section id="origins" className="relative py-24 border-t" style={{ borderColor: "var(--bf-line-medium)" }} data-testid="home-origins">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <SectionLabel index="02">Why BrandFinish was built</SectionLabel>
          <h2 className="font-editorial text-[40px] lg:text-[52px] leading-[1.05] tracking-[-0.02em] mt-6">
            Tired of moodboards<br />pretending to be brands.
          </h2>
        </div>
        <div className="lg:col-span-7 space-y-6 text-[15.5px] leading-[1.75]" style={{ color: "var(--bf-fg-muted)" }}>
          <p>
            BrandFinish came out of a frustration shared with every brand designer who has
            shipped real work: the tools available either generated a single, shallow logo
            and called it a brand – or asked you to wire up Figma, a colour calculator,
            a token script, a contrast checker and three handover documents by hand.
          </p>
          <p>
            The first version was an internal tool used on actual client projects.
            Real briefs, real archetype discovery, real accessibility constraints,
            real handover packs. Every shortcut someone wanted was a feature; every
            judgement we had to make twice was a system. That iteration loop is what you see
            in the Workspace today.
          </p>
          <p>
            BrandFinish is opinionated where opinions matter – twelve scored concepts across
            three routes (4 wordmarks, 4 symbols, and 4 hybrid marks), OKLCH for colour,
            scoring rubrics on every mark, watermarked free exports –
            and unopinionated where designers want control: every concept can be regenerated
            from a prompt, every colour family can be locked or overridden, every token can be
            edited and re-exported.
          </p>
          <p>
            It is not an "AI brand magic" wrapper. It is a strategy-led brand operating system,
            built by a practitioner, for the kind of brand work that has to survive a real
            client review.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ----------------------------------------------------------------------- */
/* SECTION 4 – FEATURES                                                     */
/* ----------------------------------------------------------------------- */
function FeatureCard({ icon: Icon, title, body, value, visual, testId }) {
  return (
    <div
      className="rounded-lg border bf-shadow-card p-6 flex flex-col"
      style={{ borderColor: "var(--bf-line-medium)", background: "var(--bf-surface)" }}
      data-testid={testId}
    >
      <div className="flex items-center gap-2.5 mb-4">
        <div
          className="w-9 h-9 rounded-md flex items-center justify-center"
          style={{ background: "rgba(109,93,246,0.12)", color: "var(--bf-violet)" }}
        >
          <Icon size={17} />
        </div>
        <div className="text-[15px] font-medium" style={{ color: "var(--bf-fg)" }}>{title}</div>
      </div>
      {visual && <div className="mb-4">{visual}</div>}
      <p className="text-[13.5px] leading-[1.7] mb-3" style={{ color: "var(--bf-fg-muted)" }}>{body}</p>
      <div
        className="mt-auto pt-3 border-t text-[11px] font-mono uppercase tracking-[0.16em]"
        style={{ borderColor: "var(--bf-line-soft)", color: "var(--bf-champagne)" }}
      >
        {value}
      </div>
    </div>
  );
}

function Features() {
  const features = [
    {
      icon: Sparkles,
      title: "Identity Concept Engine",
      body:
        "Twelve scored logo concepts across three routes: 4 wordmarks, 4 symbols, and 4 hybrid marks – each tied to a mined product-semantic motif. Generate twelve more on demand, refine any concept with art-direction sliders, or regenerate from a written prompt.",
      value: "Up to 12 concepts · Originality · Scalability · Brand fit · Small-size",
      visual: (
        <div className="grid grid-cols-3 gap-1.5">
          {Array.from({ length: 12 }).map((_, i) => (
            <div
              key={i}
              className="aspect-square rounded-sm border flex items-center justify-center text-[12px] font-editorial"
              style={{
                borderColor: "var(--bf-line-medium)",
                background: i === 4 ? "rgba(109,93,246,0.18)" : "var(--bf-bg)",
                color: "var(--bf-fg)",
              }}
            >
              Bf
            </div>
          ))}
        </div>
      ),
    },
    {
      icon: Type,
      title: "Typography Studio",
      body:
        "Three typography routes per project – display / body / mono – pulled from a curated foundry pool, never the same pairing twice. Live preview at every weight, with version history if you change your mind.",
      value: "3 routes · curated foundry pool · web-safe + open licences",
      visual: (
        <div className="rounded-md border p-3 space-y-2" style={{ borderColor: "var(--bf-line-medium)", background: "var(--bf-bg)" }}>
          <div className="font-editorial text-[28px] leading-none">Aa Bb Cc</div>
          <div className="text-[11px]" style={{ color: "var(--bf-fg-muted)" }}>Geist · 400 / 500 / 600</div>
        </div>
      ),
    },
    {
      icon: Palette,
      title: "Colour System Generation",
      body:
        "Bring an existing brand colour or let the engine pick one. OKLCH ramps from 50–950 across primary / secondary / accent / neutral / four status families. Three modes: balanced, brand-preserved, accessibility-optimised. Lock any family.",
      value: "8 families · 11-stop ramps · WCAG AA enforced · CSS / SCSS / Tailwind out",
      visual: (
        <div className="grid grid-cols-11 gap-0.5">
          {["#F4F1FE","#E5DFFC","#C7BCF8","#A48EF1","#8470EC","#6D5DF6","#594BD8","#4639B4","#332888","#251D5C","#170F36"].map((c) => (
            <div key={c} className="aspect-square rounded-sm" style={{ background: c }} />
          ))}
        </div>
      ),
    },
    {
      icon: ShieldCheck,
      title: "Brand QA",
      body:
        "Every generated system runs through a rule-based QA pass – palette health across accessibility, harmony, distinctiveness, UI suitability, brand preservation and status clarity, plus typography pairing checks. Issues come back as actionable recommendations, never opaque scores.",
      value: "6 health categories · rule-based · zero LLM cost",
    },
    {
      icon: Accessibility,
      title: "Accessibility Stress Test",
      body:
        "Stress-test every important pairing – body on background, button on accent, link on surface – at AA and AAA. Surfaces failures before client review, suggests the nearest compliant stop on the same ramp.",
      value: "AA + AAA targets · auto-suggest fixes",
    },
    {
      icon: Download,
      title: "Export Centre",
      body:
        "Real files: SVG logos with monochrome / reversed / favicon variants, design tokens (JSON), CSS variables, SCSS variables, a Tailwind config fragment, a markdown brand guide and a ZIP asset pack. Free exports are watermarked.",
      value: "SVG · CSS · SCSS · Tailwind · tokens · markdown · ZIP",
    },
    {
      icon: Layers,
      title: "Project Variants",
      body:
        "Spin up a variant of any system – sub-brand, alternate route, audience pivot – without losing the parent. Compare side by side and promote a variant to canonical when it wins the room.",
      value: "Up to 5 variants on Agency · 20 on Enterprise",
    },
    {
      icon: FileText,
      title: "Brand Discovery Inputs",
      body:
        "A structured intake – client name, industry, audience profile, four style scales, brand archetype mix, accessibility requirements, delivery channels, existing brand colours. The same input every time means generations get sharper as you go.",
      value: "3-step wizard · style scales · archetypes · audience profile",
    },
  ];
  return (
    <section id="features" className="relative py-24 border-t" style={{ borderColor: "var(--bf-line-medium)" }} data-testid="home-features">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <SectionLabel index="03">Core capabilities</SectionLabel>
        <h2 className="mt-6 font-editorial text-[44px] lg:text-[56px] leading-[1.02] tracking-[-0.02em]">
          Eight engines.<br />
          <em className="not-italic" style={{ color: "var(--bf-violet)" }}>One coherent system.</em>
        </h2>
        <p className="mt-6 text-[15px] leading-[1.7] max-w-[680px]" style={{ color: "var(--bf-fg-muted)" }}>
          Each capability is a real piece of software with real outputs. Click into the Workspace
          and the same engines you read about here are wired straight into your project.
        </p>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {features.map((f) => (
            <FeatureCard key={f.title} {...f} testId={`feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ----------------------------------------------------------------------- */
/* SECTION 5 – HOW IT WORKS                                                 */
/* ----------------------------------------------------------------------- */
function HowItWorks() {
  const steps = [
    { n: "01", t: "Discover", b: "Intake the client. Personality chips, four style scales, archetype mix, audience profile, accessibility requirements, existing brand colours." },
    { n: "02", t: "Generate", b: "One Claude-grounded pass produces a complete system – strategy, twelve scored logo concepts, typography routes, OKLCH palette, tokens, exports." },
    { n: "03", t: "Refine", b: "Save and reject concepts. Pull art-direction sliders. Override any colour family. Regenerate any logo from a written prompt. Version history is automatic." },
    { n: "04", t: "Validate", b: "Brand QA across six categories. Accessibility Stress Test on every important pairing. Surface issues with the recommended fix already in scope." },
    { n: "05", t: "Export", b: "SVG, CSS, SCSS, Tailwind, JSON tokens, a markdown brand guide and a ZIP asset pack. Optional public share link for client review." },
  ];
  return (
    <section id="how-it-works" className="relative py-24 border-t" style={{ borderColor: "var(--bf-line-medium)" }} data-testid="home-how-it-works">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <SectionLabel index="04">How it works</SectionLabel>
        <h2 className="mt-6 font-editorial text-[44px] lg:text-[56px] leading-[1.02] tracking-[-0.02em]">
          From brief<br /><em className="not-italic" style={{ color: "var(--bf-violet)" }}>to handover</em>, in five.
        </h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-5 gap-3">
          {steps.map((s, i) => (
            <div
              key={s.n}
              className="rounded-lg border bf-shadow-card p-5 relative"
              style={{ borderColor: "var(--bf-line-medium)", background: "var(--bf-surface)" }}
              data-testid={`step-${s.t.toLowerCase()}`}
            >
              <div className="text-[10px] font-mono uppercase tracking-[0.22em]" style={{ color: "var(--bf-violet)" }}>{s.n}</div>
              <div className="font-editorial text-[26px] tracking-[-0.01em] mt-3 mb-3" style={{ color: "var(--bf-fg)" }}>{s.t}</div>
              <p className="text-[12.5px] leading-[1.65]" style={{ color: "var(--bf-fg-muted)" }}>{s.b}</p>
              {i < steps.length - 1 && (
                <ArrowRight
                  size={14}
                  className="hidden md:block absolute -right-2.5 top-7"
                  style={{ color: "var(--bf-fg-subtle)" }}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ----------------------------------------------------------------------- */
/* SECTION 6 – PRICING                                                      */
/* ----------------------------------------------------------------------- */
function PricingSection({ catalog, user }) {
  const [interval, setInterval] = useState("monthly");
  const tiers = catalog?.tiers || [];

  function priceFor(t) {
    if (t.id === "freelancer") return 0;
    return interval === "annual" ? Number(t.annual_monthly_equivalent_gbp) : Number(t.monthly_price_gbp);
  }
  function billedLine(t) {
    if (t.id === "freelancer") return "free forever";
    return interval === "annual"
      ? `Billed annually · £${Number(t.annual_price_gbp).toFixed(2)}/yr`
      : "Billed monthly · cancel anytime";
  }
  function fmt(n) {
    return Number(n).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  return (
    <section id="pricing" className="relative py-24 border-t" style={{ borderColor: "var(--bf-line-medium)" }} data-testid="home-pricing">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <SectionLabel index="05">Subscription plans</SectionLabel>
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-7">
            <h2 className="font-editorial text-[44px] lg:text-[56px] leading-[1.02] tracking-[-0.02em]">
              Plans that grow<br /><em className="not-italic" style={{ color: "var(--bf-violet)" }}>with the work</em>.
            </h2>
            <p className="mt-5 text-[15px] leading-[1.7] max-w-[560px]" style={{ color: "var(--bf-fg-muted)" }}>
              GBP, billed monthly or annually via Stripe. Freelancer is free, no card required.
            </p>
          </div>
          <div className="lg:col-span-5 flex lg:justify-end">
            <div role="tablist" data-testid="pricing-interval-toggle" className="inline-flex items-center rounded-full border p-1" style={{ borderColor: "var(--bf-line-medium)", background: "var(--bf-surface)" }}>
              <button
                role="tab"
                aria-selected={interval === "monthly"}
                onClick={() => setInterval("monthly")}
                data-testid="pricing-interval-monthly"
                className={`px-4 py-1.5 text-[11px] font-mono uppercase tracking-[0.18em] rounded-full transition-colors ${interval === "monthly" ? "bf-btn-primary" : ""}`}
                style={interval === "monthly" ? {} : { color: "var(--bf-fg-muted)" }}
              >
                Monthly
              </button>
              <button
                role="tab"
                aria-selected={interval === "annual"}
                onClick={() => setInterval("annual")}
                data-testid="pricing-interval-annual"
                className={`relative px-4 py-1.5 text-[11px] font-mono uppercase tracking-[0.18em] rounded-full transition-colors ${interval === "annual" ? "bf-btn-primary" : ""}`}
                style={interval === "annual" ? {} : { color: "var(--bf-fg-muted)" }}
              >
                Annual
                <span
                  className="ml-1.5 inline-block text-[8px] font-mono align-middle px-1.5 py-0.5 rounded-sm"
                  style={{
                    background: interval === "annual" ? "var(--bf-violet)" : "rgba(109,93,246,0.18)",
                    color: interval === "annual" ? "var(--bf-porcelain)" : "var(--bf-violet)",
                  }}
                >
                  2 mo free
                </span>
              </button>
            </div>
          </div>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5" data-testid="pricing-tiers">
          {tiers.map((t) => {
            const recommended = t.is_recommended;
            const forTeams = t.is_for_teams;
            const price = priceFor(t);
            const Icon = { launch: ShieldCheck, creator: Sparkles, studio: Crown, pro: Building2 }[t.id] || Sparkles;
            return (
              <div
                key={t.id}
                data-testid={`home-tier-${t.id}`}
                className={`relative flex flex-col rounded-lg p-7 bf-shadow-card transition-all ${
                  recommended ? "border-2" : "border hover:-translate-y-0.5"
                }`}
                style={{
                  background: recommended ? "linear-gradient(180deg, rgba(109,93,246,0.08), var(--bf-surface) 55%)" : "var(--bf-surface)",
                  borderColor: recommended ? "var(--bf-violet)" : "var(--bf-line-medium)",
                }}
              >
                {recommended && (
                  <div data-testid={`home-tier-recommended-${t.id}`} className="absolute -top-3 left-6 text-[9px] font-mono uppercase tracking-[0.24em] px-2.5 py-1 rounded-sm" style={{ background: "var(--bf-violet)", color: "var(--bf-porcelain)" }}>
                    Most popular
                  </div>
                )}
                {forTeams && !recommended && (
                  <div data-testid={`home-tier-for-teams-${t.id}`} className="absolute -top-3 left-6 text-[9px] font-mono uppercase tracking-[0.24em] px-2.5 py-1 rounded-sm border" style={{ borderColor: "var(--bf-champagne)", color: "var(--bf-champagne)", background: "var(--bf-bg)" }}>
                    For teams
                  </div>
                )}
                <div className="flex items-center gap-2" style={{ color: recommended ? "var(--bf-violet)" : "var(--bf-fg-muted)" }}>
                  <Icon size={14} />
                  <span className="text-[10px] font-mono uppercase tracking-[0.24em]">{t.name}</span>
                </div>
                <div className="mt-5 flex items-baseline gap-1">
                  <span className="text-[13px] font-mono" style={{ color: "var(--bf-fg-subtle)" }}>£</span>
                  <span className="font-editorial text-[54px] leading-none tracking-[-0.015em]" data-testid={`home-tier-price-${t.id}`}>
                    {Math.floor(price)}
                  </span>
                  <span className="text-[13px] font-mono" style={{ color: "var(--bf-fg-subtle)" }}>
                    .{(price.toFixed(2).split(".")[1] || "00")}
                  </span>
                  <span className="text-[#737373] text-xs font-mono ml-1">/ mo</span>
                </div>
                <div className="mt-1 text-[11px] font-mono uppercase tracking-[0.16em]" style={{ color: "var(--bf-fg-subtle)" }}>
                  {billedLine(t)}
                </div>
                {t.id !== "freelancer" && interval === "annual" && Number(t.annual_savings_pct) > 0 && (
                  <div className="mt-2 inline-flex items-center self-start gap-1 text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-0.5 rounded-sm border" style={{ borderColor: "var(--bf-violet)", color: "var(--bf-violet)" }}>
                    Save {t.annual_savings_pct}% vs monthly
                  </div>
                )}
                {t.tagline && (
                  <div className="mt-4 text-[13px]" style={{ color: "var(--bf-fg-muted)" }}>{t.tagline}</div>
                )}
                <ul className="mt-5 space-y-2.5 flex-1 border-t pt-5" style={{ borderColor: "var(--bf-line-soft)" }}>
                  {(t.features || []).slice(0, 6).map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-[13px]" style={{ color: "var(--bf-fg)" }}>
                      <Check size={13} className="mt-1 shrink-0" style={{ color: recommended ? "var(--bf-violet)" : "var(--bf-champagne)" }} />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to={user ? "/billing" : "/register"}
                  data-testid={`home-tier-cta-${t.id}`}
                  className={`mt-7 w-full px-4 py-3 text-sm font-semibold rounded-md text-center transition-all ${
                    t.id === "freelancer" ? "" : "bf-btn-primary"
                  }`}
                  style={t.id === "freelancer" ? { border: "1px solid var(--bf-line-medium)", color: "var(--bf-fg-muted)" } : {}}
                >
                  {t.id === "freelancer" ? "Start free" : `Choose ${t.name}`}
                </Link>
              </div>
            );
          })}
          {!tiers.length && (
            <div className="col-span-full text-[#737373] font-mono text-sm" data-testid="home-pricing-loading">loading pricing…</div>
          )}
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5 text-[13px]" data-testid="home-pricing-trust">
          <div className="rounded-lg border p-5" style={{ borderColor: "var(--bf-line-medium)", background: "var(--bf-surface)" }}>
            <div className="flex items-center gap-2 mb-2" style={{ color: "var(--bf-violet)" }}><Lock size={13} /><span className="text-[10px] font-mono uppercase tracking-[0.22em]">Stripe-secure payments</span></div>
            <p style={{ color: "var(--bf-fg-muted)" }}>Card details never touch our servers. Stripe-hosted Checkout, PCI-compliant by construction. Refunds, receipts and tax handled by Stripe.</p>
          </div>
          <div className="rounded-lg border p-5" style={{ borderColor: "var(--bf-line-medium)", background: "var(--bf-surface)" }}>
            <div className="flex items-center gap-2 mb-2" style={{ color: "var(--bf-violet)" }}><Crown size={13} /><span className="text-[10px] font-mono uppercase tracking-[0.22em]">Cancel anytime</span></div>
            <p style={{ color: "var(--bf-fg-muted)" }}>Cancel anytime from the Stripe Customer Portal – your subscription stops at the end of the current period.</p>
          </div>
          <div className="rounded-lg border p-5" style={{ borderColor: "var(--bf-line-medium)", background: "var(--bf-surface)" }}>
            <div className="flex items-center gap-2 mb-2" style={{ color: "var(--bf-violet)" }}><Users size={13} /><span className="text-[10px] font-mono uppercase tracking-[0.22em]">Self-serve billing</span></div>
            <p style={{ color: "var(--bf-fg-muted)" }}>Update card, change plan, switch to annual or download VAT invoices via the Stripe Customer Portal – accessible from your Billing screen at any time.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ----------------------------------------------------------------------- */
/* SECTION 7 – TRUST                                                        */
/* ----------------------------------------------------------------------- */
function Trust() {
  const items = [
    { t: "Built by a practitioner", b: "Designed by someone who has shipped brand systems on real client work – not a generic SaaS founder discovering the design industry six months ago." },
    { t: "Real product, not an AI wrapper", b: "BrandFinish is structured software. The LLM is one input among many: discovery, palette engine, scoring rubrics, accessibility checks, export pipeline are all deterministic." },
    { t: "Security-conscious architecture", b: "JWT auth with bcrypt password hashing, Stripe-hosted card capture, MongoDB with project-scoped queries. No card details, ever. No third-party trackers in the workspace." },
    { t: "You own your work", b: "Generated assets are yours to use commercially, including for client work, on every paid plan. Free-tier exports are watermarked. We never claim a licence in your output." },
    { t: "Roadmap, not vapourware", b: "Public, prioritised backlog: PDF/DOCX exports, white-label client handoff packs, multi-seat workspaces, Figma integration. Shipped milestones in the changelog." },
    { t: "British English, GBP-native", b: "Pricing, palette ramps and copy are British English by default – colour, not color. GBP billing first, multi-currency on the roadmap." },
  ];
  return (
    <section id="trust" className="relative py-24 border-t" style={{ borderColor: "var(--bf-line-medium)" }} data-testid="home-trust">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <SectionLabel index="06">Why trust BrandFinish</SectionLabel>
        <h2 className="mt-6 font-editorial text-[44px] lg:text-[56px] leading-[1.02] tracking-[-0.02em]">
          Made for designers,<br /><em className="not-italic" style={{ color: "var(--bf-violet)" }}>by a designer-engineer</em>.
        </h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it) => (
            <div key={it.t} className="rounded-lg border bf-shadow-card p-6" style={{ borderColor: "var(--bf-line-medium)", background: "var(--bf-surface)" }}>
              <div className="text-[15px] font-medium mb-2" style={{ color: "var(--bf-fg)" }}>{it.t}</div>
              <p className="text-[13.5px] leading-[1.7]" style={{ color: "var(--bf-fg-muted)" }}>{it.b}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ----------------------------------------------------------------------- */
/* SECTION 8 – ABOUT THE CREATOR                                            */
/* ----------------------------------------------------------------------- */
function About() {
  return (
    <section id="about" className="relative py-24 border-t" style={{ borderColor: "var(--bf-line-medium)" }} data-testid="home-about">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-4">
          <SectionLabel index="07">About the creator</SectionLabel>
          <div
            className="mt-6 aspect-[4/5] rounded-lg border bf-shadow-card overflow-hidden relative max-w-[80%]"
            style={{ borderColor: "var(--bf-line-medium)", background: "var(--bf-surface)" }}
            data-testid="founder-portrait"
          >
            <div className="absolute inset-0 bf-canvas opacity-30" />
            <img
              src="/images/profile-320w.webp"
              srcSet="/images/profile-320w.webp 320w"
              alt="Simon Jackson – Solution Architect, founder of BrandFinish"
              loading="lazy"
              width="640"
              height="800"
              className="absolute inset-0 w-full h-full object-cover object-center"
              data-testid="founder-portrait-image"
            />
            <div
              className="absolute inset-x-0 bottom-0 p-5 pt-12"
              style={{ background: "linear-gradient(180deg, transparent 0%, rgba(10,10,11,0.85) 60%, rgba(10,10,11,0.95) 100%)" }}
            >
              <div className="font-editorial text-[24px] tracking-[-0.01em]" style={{ color: "var(--bf-porcelain)" }}>Simon Jackson</div>
              <div className="mt-1 text-[10px] font-mono uppercase tracking-[0.22em]" style={{ color: "var(--bf-fg-subtle)" }}>
                BSc · CISSP · MBCS · Solution Architect
              </div>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <a
              href="https://www.linkedin.com/in/sjackson0109/"
              target="_blank"
              rel="noreferrer"
              data-testid="founder-linkedin"
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-[11px] font-mono uppercase tracking-[0.16em] border rounded-md transition-colors"
              style={{ borderColor: "var(--bf-line-medium)", color: "var(--bf-fg-muted)" }}
            >
              <Linkedin size={12} /> LinkedIn
            </a>
            <a
              href="https://simon.jacksonfamily.me"
              target="_blank"
              rel="noreferrer"
              data-testid="founder-personal-site"
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-[11px] font-mono uppercase tracking-[0.16em] border rounded-md transition-colors"
              style={{ borderColor: "var(--bf-line-medium)", color: "var(--bf-fg-muted)" }}
            >
              <ExternalLink size={12} /> Personal site
            </a>
          </div>
        </div>
        <div className="lg:col-span-8 space-y-5 text-[15.5px] leading-[1.75]" style={{ color: "var(--bf-fg-muted)" }}>
          <h2 className="font-editorial text-[44px] lg:text-[56px] leading-[1.02] tracking-[-0.02em]" style={{ color: "var(--bf-fg)" }}>
            Built by Simon Jackson.
          </h2>
          <p>
            Simon is a technology strategist and hands-on solutions architect with more than
            two decades of experience delivering outcomes across cloud, security, and modern
            infrastructure. He holds a <strong style={{ color: "var(--bf-fg)" }}>CISSP</strong>,
            a postgraduate qualification in <strong style={{ color: "var(--bf-fg)" }}>Cloud Computing</strong>{" "}
            from Caltech's Centre for Technology &amp; Management Education,{" "}
            <strong style={{ color: "var(--bf-fg)" }}>Chartered IT Professional (CITP)</strong> status
            with the BCS, and a BSc in Physics – alongside a deep portfolio of certifications
            across AWS, Azure, GCP, GitHub and on-premise platforms.
          </p>
          <p>
            BrandFinish came out of that engineering pedigree colliding with a long-running
            side practice in brand and visual identity. The first version was an internal tool
            used on real client work – a way to replace the chaotic stack of moodboard apps,
            colour calculators, ad-hoc Figma files and copy-paste handover docs with one
            structured workflow that produced exports other people could actually open.
          </p>
          <p>
            The philosophy is straightforward: brand identity software should be opinionated
            where opinions are useful (12 concepts: 4 wordmarks, 4 symbols, 4 hybrid marks –
            OKLCH for colour,
            scored outputs, watermarked free exports) and unopinionated where designers want
            control (every concept regenerable from a prompt, every colour family lockable,
            every token editable). The product earns its keep when it removes a judgement
            you would have had to make twice.
          </p>
          <p>
            BrandFinish is independent, self-funded for now, and shipped publicly – built
            with the same pragmatic, outcome-focused discipline Simon brings to every
            architecture engagement.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ----------------------------------------------------------------------- */
/* SECTION 9 – INVESTORS / CONTRIBUTORS                                     */
/* ----------------------------------------------------------------------- */
function InvestorsContributors() {
  return (
    <section id="invest" className="relative py-24 border-t" style={{ borderColor: "var(--bf-line-medium)" }} data-testid="home-invest">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <SectionLabel index="08">Investors & contributors</SectionLabel>
        <h2 className="mt-6 font-editorial text-[40px] lg:text-[52px] leading-[1.05] tracking-[-0.02em]">
          Looking for investors<br /><em className="not-italic" style={{ color: "var(--bf-violet)" }}>and contributors</em>.
        </h2>
        <p className="mt-5 text-[15px] leading-[1.7] max-w-[640px]" style={{ color: "var(--bf-fg-muted)" }}>
          BrandFinish is independently built. If you want to back the next chapter – capital,
          design, engineering or brand strategy – there are two open tracks.
        </p>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="rounded-lg border bf-shadow-card p-7" style={{ borderColor: "var(--bf-line-medium)", background: "var(--bf-surface)" }} data-testid="invest-track-investors">
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] mb-3" style={{ color: "var(--bf-violet)" }}>Investors</div>
            <div className="font-editorial text-[28px] tracking-[-0.01em] mb-4">Early-stage product growth.</div>
            <ul className="space-y-2 text-[13.5px] leading-[1.7]" style={{ color: "var(--bf-fg-muted)" }}>
              <li className="flex gap-2"><Check size={13} className="mt-1.5 shrink-0" style={{ color: "var(--bf-violet)" }} /> Seed conversations welcome – pre-seed and angel rounds considered.</li>
              <li className="flex gap-2"><Check size={13} className="mt-1.5 shrink-0" style={{ color: "var(--bf-violet)" }} /> Strategic partnerships with brand consultancies, design education, or print fulfilment.</li>
              <li className="flex gap-2"><Check size={13} className="mt-1.5 shrink-0" style={{ color: "var(--bf-violet)" }} /> Practitioner cap table – designers, brand leads, creative agency operators preferred.</li>
            </ul>
            <a href={`mailto:invest@${DOMAIN}`} data-testid="invest-cta-investors" className="mt-6 inline-flex items-center gap-1.5 bf-btn-primary px-4 py-2.5 text-[12px] font-semibold rounded-md">
              Get in touch <ArrowUpRight size={13} />
            </a>
          </div>
          <div className="rounded-lg border bf-shadow-card p-7" style={{ borderColor: "var(--bf-line-medium)", background: "var(--bf-surface)" }} data-testid="invest-track-contributors">
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] mb-3" style={{ color: "var(--bf-champagne)" }}>Contributors</div>
            <div className="font-editorial text-[28px] tracking-[-0.01em] mb-4">Designers, engineers, strategists.</div>
            <ul className="space-y-2 text-[13.5px] leading-[1.7]" style={{ color: "var(--bf-fg-muted)" }}>
              <li className="flex gap-2"><Check size={13} className="mt-1.5 shrink-0" style={{ color: "var(--bf-champagne)" }} /> Type designers – curated foundry pool, pairing rules.</li>
              <li className="flex gap-2"><Check size={13} className="mt-1.5 shrink-0" style={{ color: "var(--bf-champagne)" }} /> Front-end engineers – workspace, exports, accessibility tooling.</li>
              <li className="flex gap-2"><Check size={13} className="mt-1.5 shrink-0" style={{ color: "var(--bf-champagne)" }} /> Brand strategists – discovery rubrics, archetype frameworks.</li>
              <li className="flex gap-2"><Check size={13} className="mt-1.5 shrink-0" style={{ color: "var(--bf-champagne)" }} /> Open collaboration: paid contracts and equity-aligned roles both possible.</li>
            </ul>
            <a href={`mailto:hello@${DOMAIN}`} data-testid="invest-cta-contributors" className="mt-6 inline-flex items-center gap-1.5 px-4 py-2.5 text-[12px] font-mono uppercase tracking-[0.18em] border rounded-md" style={{ borderColor: "var(--bf-line-medium)", color: "var(--bf-fg)" }}>
              Say hello <ArrowUpRight size={13} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ----------------------------------------------------------------------- */
/* SECTION 10 – FAQ                                                         */
/* ----------------------------------------------------------------------- */
function FAQItem({ id, q, a, seeAlso, itemsById, isOpen, onToggle, onJump, testId }) {
  return (
    <div
      id={`faq-anchor-${id}`}
      className="border-b scroll-mt-[120px]"
      style={{ borderColor: "var(--bf-line-medium)" }}
      data-testid={testId}
    >
      <button
        type="button"
        onClick={() => onToggle(id)}
        className="w-full flex items-start justify-between gap-6 py-5 text-left group"
        aria-expanded={isOpen}
      >
        <span className="text-[15.5px] font-medium leading-[1.4]" style={{ color: "var(--bf-fg)" }}>{q}</span>
        <ChevronDown
          size={18}
          className="mt-1 shrink-0 transition-transform"
          style={{
            color: "var(--bf-fg-subtle)",
            transform: isOpen ? "rotate(180deg)" : "rotate(0)",
          }}
        />
      </button>
      {isOpen && (
        <div className="pb-6 max-w-[820px]">
          <div className="text-[14px] leading-[1.75]" style={{ color: "var(--bf-fg-muted)" }}>
            {a}
          </div>
          {seeAlso && seeAlso.length > 0 && (
            <div
              className="mt-5 pt-4 border-t"
              style={{ borderColor: "var(--bf-line-medium)" }}
              data-testid={`${testId}-see-also`}
            >
              <div
                className="text-[10px] font-mono uppercase tracking-[0.22em] mb-2"
                style={{ color: "var(--bf-fg-subtle)" }}
              >
                See also
              </div>
              <ul className="space-y-1.5">
                {seeAlso.map((refId) => {
                  const ref = itemsById[refId];
                  if (!ref) return null;
                  return (
                    <li key={refId}>
                      <button
                        type="button"
                        onClick={() => onJump(refId)}
                        className="text-[13px] leading-[1.5] underline-offset-4 hover:underline text-left"
                        style={{ color: "var(--bf-violet)" }}
                        data-testid={`${testId}-see-also-${refId}`}
                      >
                        {ref.q}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/* FAQ structured-data injector – FAQPage + BreadcrumbList                 */
/* ----------------------------------------------------------------------- */
function FaqStructuredData() {
  useEffect(() => {
    const SITE_URL = `https://${DOMAIN}/`;
    const faqScript = document.createElement("script");
    faqScript.type = "application/ld+json";
    faqScript.dataset.bfStructured = "faq";
    faqScript.textContent = JSON.stringify(buildFaqJsonLd({ pageUrl: SITE_URL }));

    const crumbScript = document.createElement("script");
    crumbScript.type = "application/ld+json";
    crumbScript.dataset.bfStructured = "breadcrumb";
    crumbScript.textContent = JSON.stringify(buildBreadcrumbJsonLd({ pageUrl: SITE_URL }));

    document.head.appendChild(faqScript);
    document.head.appendChild(crumbScript);
    return () => {
      faqScript.remove();
      crumbScript.remove();
    };
  }, []);
  return null;
}

function FAQ() {
  const groups = FAQ_GROUPS;

  // Build flat lookup for cross-link resolution
  const itemsById = {};
  groups.forEach((g) => g.items.forEach((it) => { itemsById[it.id] = { ...it, cat: g.cat }; }));

  const [openId, setOpenId] = useState(null);

  const onToggle = (id) => setOpenId((cur) => (cur === id ? null : id));
  const onJump = (id) => {
    setOpenId(id);
    requestAnimationFrame(() => {
      const el = document.getElementById(`faq-anchor-${id}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const slug = (cat) => cat.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

  return (
    <section id="faq" className="relative py-24 border-t" style={{ borderColor: "var(--bf-line-medium)" }} data-testid="home-faq">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <SectionLabel index="09">FAQ</SectionLabel>
        <h2 className="mt-6 font-editorial text-[44px] lg:text-[56px] leading-[1.02] tracking-[-0.02em]">
          Answers, before<br /><em className="not-italic" style={{ color: "var(--bf-violet)" }}>you have to ask</em>.
        </h2>
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-3">
            <ul className="space-y-2 sticky top-[88px]" data-testid="faq-cat-nav">
              {groups.map((g) => (
                <li key={g.cat}>
                  <a
                    href={`#faq-${slug(g.cat)}`}
                    className="block text-[12px] font-mono uppercase tracking-[0.18em] py-1.5 transition-colors hover:text-[var(--bf-fg)]"
                    style={{ color: "var(--bf-fg-muted)" }}
                    data-testid={`faq-cat-link-${slug(g.cat)}`}
                  >
                    {g.cat}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-9">
            {groups.map((g) => (
              <div key={g.cat} id={`faq-${slug(g.cat)}`} className="mb-10 scroll-mt-[120px]" data-testid={`faq-group-${slug(g.cat)}`}>
                <div className="text-[10px] font-mono uppercase tracking-[0.22em] mb-3" style={{ color: "var(--bf-violet)" }}>
                  {g.cat}
                </div>
                <div>
                  {g.items.map((it, idx) => (
                    <FAQItem
                      key={it.id}
                      id={it.id}
                      q={it.q}
                      a={it.a}
                      seeAlso={it.seeAlso}
                      itemsById={itemsById}
                      isOpen={openId === it.id}
                      onToggle={onToggle}
                      onJump={onJump}
                      testId={`faq-item-${slug(g.cat)}-${idx}`}
                    />
                  ))}
                </div>
              </div>
            ))}
            <div className="rounded-lg border p-5 mt-4 flex items-start gap-3" style={{ borderColor: "var(--bf-line-medium)", background: "var(--bf-surface)" }}>
              <MessageCircle size={18} className="mt-0.5 shrink-0" style={{ color: "var(--bf-violet)" }} />
              <div className="text-[13.5px] leading-[1.7]" style={{ color: "var(--bf-fg-muted)" }}>
                Still have a question? Email <a className="underline" style={{ color: "var(--bf-fg)" }} href={`mailto:hello@${DOMAIN}`}>hello@{DOMAIN}</a> – replies typically within one business day.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ----------------------------------------------------------------------- */
/* SECTION 11 – FINAL CTA + FOOTER                                          */
/* ----------------------------------------------------------------------- */
function FinalCTA({ user }) {
  return (
    <section className="relative py-28 border-t overflow-hidden" style={{ borderColor: "var(--bf-line-medium)" }} data-testid="home-final-cta">
      <div className="absolute inset-0 pointer-events-none bf-canvas opacity-50" />
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 50% 50%, rgba(109,93,246,0.16), transparent 60%)" }} />
      <div className="relative max-w-[1100px] mx-auto px-6 md:px-10 text-center">
        <SectionLabel index="10">Get started</SectionLabel>
        <h2 className="mt-6 font-editorial text-[52px] lg:text-[80px] leading-[0.98] tracking-[-0.025em]">
          Design your first<br /><em className="not-italic" style={{ color: "var(--bf-violet)" }}>brand system today</em>.
        </h2>
        <p className="mt-7 text-[16px] leading-[1.7] max-w-[560px] mx-auto" style={{ color: "var(--bf-fg-muted)" }}>
          Start free on Freelancer — no card required, no expiry.
          Upgrade to Boutique, Agency or Enterprise whenever you're ready.
        </p>
        <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
          <PrimaryCTA to={user ? "/dashboard" : "/register"} testId="final-cta-primary">
            Get started <ArrowRight size={16} />
          </PrimaryCTA>
          <GhostCTA href="#pricing" testId="final-cta-secondary">
            View pricing <ArrowUpRight size={15} />
          </GhostCTA>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="border-t" style={{ borderColor: "var(--bf-line-medium)" }} data-testid="home-footer">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12 grid grid-cols-2 md:grid-cols-5 gap-8 text-[13px]" style={{ color: "var(--bf-fg-muted)" }}>
        <div className="col-span-2">
          <div className="flex items-center gap-2.5 mb-4">
            <BrandMonogram size={28} />
            <div>
              <div className="font-editorial text-[18px] tracking-[-0.01em]" style={{ color: "var(--bf-fg)" }}>BrandFinish</div>
              <div className="text-[9px] font-mono uppercase tracking-[0.28em]" style={{ color: "var(--bf-fg-subtle)" }}>Studio</div>
            </div>
          </div>
          <p className="leading-[1.7] max-w-[360px]">
            Strategy-led brand identity software. Built for designers who ship real client work.
            GBP, Stripe, accessible by construction.
          </p>
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.22em] mb-3" style={{ color: "var(--bf-fg-subtle)" }}>Product</div>
          <ul className="space-y-2">
            <li><a href="#features" className="hover:text-[var(--bf-fg)] transition-colors">Features</a></li>
            <li><a href="#how-it-works" className="hover:text-[var(--bf-fg)] transition-colors">How it works</a></li>
            <li><a href="#pricing" className="hover:text-[var(--bf-fg)] transition-colors">Pricing</a></li>
            <li><a href="#faq" className="hover:text-[var(--bf-fg)] transition-colors">FAQ</a></li>
          </ul>
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.22em] mb-3" style={{ color: "var(--bf-fg-subtle)" }}>Company</div>
          <ul className="space-y-2">
            <li><a href="#about" className="hover:text-[var(--bf-fg)] transition-colors">About</a></li>
            <li><a href="#invest" className="hover:text-[var(--bf-fg)] transition-colors">Invest</a></li>
            <li><a href="mailto:hello@brandfinish.com" className="hover:text-[var(--bf-fg)] transition-colors">Contact</a></li>
          </ul>
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.22em] mb-3" style={{ color: "var(--bf-fg-subtle)" }}>Account</div>
          <ul className="space-y-2">
            <li><Link to="/login" className="hover:text-[var(--bf-fg)] transition-colors">Sign in</Link></li>
            <li><Link to="/register" className="hover:text-[var(--bf-fg)] transition-colors">Get started</Link></li>
            <li><Link to="/billing" className="hover:text-[var(--bf-fg)] transition-colors">Billing</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t" style={{ borderColor: "var(--bf-line-medium)" }}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-[11px] font-mono uppercase tracking-[0.18em]" style={{ color: "var(--bf-fg-subtle)" }}>
          <div>© {year} BrandFinish Studio · GBP · Stripe-secured · Built in the UK</div>
          <div className="flex items-center gap-5">
            <a href="#" className="hover:text-[var(--bf-fg)] transition-colors">Privacy</a>
            <a href="#" className="hover:text-[var(--bf-fg)] transition-colors">Terms</a>
            <a href="https://www.linkedin.com/in/sjackson0109/" target="_blank" rel="noreferrer" className="hover:text-[var(--bf-fg)] transition-colors inline-flex items-center gap-1.5"><Linkedin size={11} /> LinkedIn</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ----------------------------------------------------------------------- */
/* PAGE EXPORT                                                              */
/* ----------------------------------------------------------------------- */
export default function Home() {
  const { user } = useAuth();
  const [catalog, setCatalog] = useState(null);
  const navigate = useNavigate();
  void navigate; // reserved for future deep-links

  useEffect(() => {
    api.get("/billing/catalog").then((r) => setCatalog(r.data)).catch(() => {
      setCatalog({ tiers: [] });
    });
  }, []);

  // Smooth scroll for in-page anchor nav.
  useEffect(() => {
    function onClick(e) {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const id = a.getAttribute("href").slice(1);
      const el = document.getElementById(id);
      if (!el) return;
      e.preventDefault();
      window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 72, behavior: "smooth" });
    }
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  return (
    <div data-testid="home-page" style={{ background: "var(--bf-bg)", color: "var(--bf-fg)" }}>
      <FaqStructuredData />
      <HomeNav user={user} />
      <Hero user={user} />
      <WhatIs />
      <Origins />
      <Features />
      <HowItWorks />
      <PricingSection catalog={catalog} user={user} />
      <Trust />
      <About />
      <InvestorsContributors />
      <FAQ />
      <FinalCTA user={user} />
      <Footer />
    </div>
  );
}
